import { useEffect, useId, useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import { type CosmaSettings, saveCosmaCredentials, saveCosmaSettings, useResource } from "./api";
import { Pillola } from "./parti";

type Props = {
  onCredentialsChanged?: () => void;
};

const FALLBACK: CosmaSettings = {
  host: "b2b.magazzinicosma.it",
  port: 21,
  partita_iva: "",
  ftp_user: "",
  remote_dir: "/Ordini/",
  sku_prefixes: [],
  password_configured: false,
  sku_count: 0,
};

const INPUT_BASE =
  "mt-1 w-full rounded-md border border-line bg-paper px-3 py-2 text-[12px] " +
  "outline-none transition-[border-color,box-shadow] duration-[120ms] " +
  "focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft " +
  "placeholder:text-ink-3 max-md:py-3 max-md:text-[15px]";

type DotTone = "green" | "amber";

function StatusDot({ tone, label }: { tone: DotTone; label: string }) {
  const dot = tone === "green" ? "bg-green" : "bg-amber";
  const text = "text-ink";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      <span className={`text-[12px] font-semibold ${text} max-md:text-[13.5px]`}>{label}</span>
    </span>
  );
}

function GroupEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2.5 flex items-center gap-2 text-[12px] font-semibold text-ink-2">
      <span>{children}</span>
      <span className="h-px flex-1 bg-line" aria-hidden="true" />
    </p>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.7] [stroke-linecap:round] [stroke-linejoin:round]"
      aria-hidden="true"
    >
      {off ? (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 5.1A10.9 10.9 0 0112 5c5 0 9.3 3.1 11 7-.6 1.4-1.5 2.7-2.6 3.7" />
          <path d="M6.1 6.2C4 7.5 2.3 9.5 1 12c1.7 3.9 6 7 11 7 1.8 0 3.5-.4 5-1.1" />
          <path d="M9.9 9.9a3 3 0 004.2 4.2" />
        </>
      ) : (
        <>
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

export function CosmaSettingsCard({ onCredentialsChanged }: Props) {
  const settings = useResource<CosmaSettings>("/api/suppliers/cosma/settings", FALLBACK);
  const ids = useId();
  const [host, setHost] = useState(FALLBACK.host);
  const [port, setPort] = useState(FALLBACK.port);
  const [partitaIva, setPartitaIva] = useState("");
  const [ftpUser, setFtpUser] = useState("");
  const [remoteDir, setRemoteDir] = useState(FALLBACK.remote_dir);
  const [skuPrefixes, setSkuPrefixes] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // dirty impedisce che il re-fetch sovrascriva gli input mentre l'utente
  // sta ancora digitando (stessa logica di AllServiceSettingsCard).
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dirty) return;
    const v = settings.value;
    setHost(v.host);
    setPort(v.port);
    setPartitaIva(v.partita_iva);
    setFtpUser(v.ftp_user);
    setRemoteDir(v.remote_dir);
    setSkuPrefixes(v.sku_prefixes.join(", "));
  }, [settings.value, dirty]);

  function edit<T>(setter: (v: T) => void): (v: T) => void {
    return (v: T) => {
      setDirty(true);
      setter(v);
    };
  }

  const parsedPrefixes = skuPrefixes
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const credentialsOk = settings.value.password_configured;
  const configOk = Boolean(settings.value.partita_iva);
  const ready = credentialsOk && configOk;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await saveCosmaSettings({
        host: host.trim(),
        port: Number(port) || 21,
        partita_iva: partitaIva.trim(),
        ftp_user: ftpUser.trim(),
        remote_dir: remoteDir.trim() || "/Ordini/",
        sku_prefixes: parsedPrefixes,
      });
      const pwd = password.trim();
      if (pwd) {
        await saveCosmaCredentials(pwd);
        setPassword("");
        setShowPassword(false);
      }
      setMessage("Impostazioni Cosma salvate.");
      setDirty(false);
      await settings.refresh();
      onCredentialsChanged?.();
    } catch (err) {
      setMessage(null);
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold leading-tight text-ink max-md:text-[17px]">
            Cosma · FTP
          </h3>
          <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2 max-md:text-[14px] max-md:text-ink">
            Invio ordini via FTP — un CSV{" "}
            <code className="rounded bg-paper-2 px-1 py-px tabular-nums text-[11px] text-ink-2">
              ;
            </code>
            -separato caricato in{" "}
            <code className="rounded bg-paper-2 px-1 py-px tabular-nums text-[11px] text-ink-2">
              /Ordini/
            </code>
            . La password sta fuori dal database (keyring sul PC in sviluppo, secret file sul
            server).
          </p>
        </div>
        <Pillola tono={ready ? "ok" : "attenzione"}>{ready ? "Pronto" : "Da configurare"}</Pillola>
      </div>

      {/* Semaforo granulare: l'operatore deve sapere QUALE parte manca, non
          solo un Pronto/Non-pronto opaco. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-paper-2 px-3 py-2">
        <StatusDot
          tone={credentialsOk ? "green" : "amber"}
          label={credentialsOk ? "Password configurata" : "Password mancante"}
        />
        <span className="h-3 w-px bg-line-2" aria-hidden="true" />
        <StatusDot
          tone={configOk ? "green" : "amber"}
          label={configOk ? "Partita IVA impostata" : "Partita IVA mancante"}
        />
        <span className="h-3 w-px bg-line-2" aria-hidden="true" />
        <StatusDot
          tone={settings.value.sku_count > 0 ? "green" : "amber"}
          label={
            settings.value.sku_count > 0
              ? `${settings.value.sku_count.toLocaleString("it-IT")} SKU Cosma a catalogo`
              : "Catalogo SKU Cosma vuoto"
          }
        />
      </div>

      <form onSubmit={(event) => void submit(event)} className="space-y-5">
        <section>
          <GroupEyebrow>Connessione FTP</GroupEyebrow>
          <div className="grid grid-cols-2 gap-3">
            <label htmlFor={`${ids}-host`} className="block text-[12px] font-medium text-ink-2">
              Host
              <input
                id={`${ids}-host`}
                type="text"
                value={host}
                onChange={(e) => edit(setHost)(e.target.value)}
                className={`${INPUT_BASE} tabular-nums`}
              />
            </label>
            <label htmlFor={`${ids}-port`} className="block text-[12px] font-medium text-ink-2">
              Porta
              <input
                id={`${ids}-port`}
                type="number"
                value={port}
                onChange={(e) => {
                  // Audit MEDIUM-1: clearing → Number("")=0; mostriamo
                  // sempre un valore valido (default 21) invece di 0.
                  const n = Number(e.target.value);
                  edit(setPort)(Number.isFinite(n) && n > 0 ? n : 21);
                }}
                className={`${INPUT_BASE} tabular-nums`}
              />
            </label>
            <label htmlFor={`${ids}-user`} className="block text-[12px] font-medium text-ink-2">
              Utente FTP
              <input
                id={`${ids}-user`}
                type="text"
                value={ftpUser}
                onChange={(e) => edit(setFtpUser)(e.target.value)}
                placeholder="default: Partita IVA"
                className={`${INPUT_BASE} tabular-nums`}
              />
            </label>
            <label htmlFor={`${ids}-pwd`} className="block text-[12px] font-medium text-ink-2">
              <span className="flex items-center justify-between gap-2">
                Password
                <small
                  className={[
                    "rounded-full px-2 py-0.5 text-[12px] font-bold",
                    credentialsOk ? "bg-green-soft text-ink" : "bg-amber-soft text-ink",
                  ].join(" ")}
                >
                  {credentialsOk ? "Configurata" : "Mancante"}
                </small>
              </span>
              <span className="relative mt-1 block">
                <input
                  id={`${ids}-pwd`}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => edit(setPassword)(e.target.value)}
                  placeholder={credentialsOk ? "•••••••• (invariata)" : "Password FTP"}
                  autoComplete="new-password"
                  className={`${INPUT_BASE} mt-0 pr-9 tabular-nums`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  title={showPassword ? "Nascondi" : "Mostra"}
                  className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-ink-2 transition-colors hover:text-accent focus-visible:text-accent focus-visible:outline-none"
                >
                  <EyeIcon off={showPassword} />
                </button>
              </span>
            </label>
          </div>
        </section>

        <section>
          <GroupEyebrow>Mappatura ordini</GroupEyebrow>
          <div className="grid grid-cols-2 gap-3">
            <label htmlFor={`${ids}-piva`} className="block text-[12px] font-medium text-ink-2">
              Partita IVA <span className="font-normal text-ink-2">(nome file, senza IT)</span>
              <input
                id={`${ids}-piva`}
                type="text"
                value={partitaIva}
                onChange={(e) => edit(setPartitaIva)(e.target.value)}
                placeholder="04486540240"
                className={`${INPUT_BASE} tabular-nums`}
              />
            </label>
            <label htmlFor={`${ids}-dir`} className="block text-[12px] font-medium text-ink-2">
              Cartella remota
              <input
                id={`${ids}-dir`}
                type="text"
                value={remoteDir}
                onChange={(e) => edit(setRemoteDir)(e.target.value)}
                className={`${INPUT_BASE} tabular-nums`}
              />
            </label>
          </div>
          <label htmlFor={`${ids}-sku`} className="mt-3 block text-[12px] font-medium text-ink-2">
            Prefissi SKU Cosma <span className="font-normal text-ink-2">(fallback)</span>
            <span className="mt-0.5 block font-normal text-[11px] text-ink-2">
              {settings.value.sku_count > 0
                ? `Il riconoscimento usa il catalogo (${settings.value.sku_count.toLocaleString("it-IT")} SKU Cosma dal Google Sheet): match esatto, precedenza sui prefissi. I prefissi servono solo se il catalogo è vuoto.`
                : "Catalogo Cosma vuoto: in attesa, gli ordini vengono riconosciuti per prefisso (separati da virgola; vuoto = tutte le righe degli ordini selezionati)."}
            </span>
            <input
              id={`${ids}-sku`}
              type="text"
              value={skuPrefixes}
              onChange={(e) => edit(setSkuPrefixes)(e.target.value)}
              placeholder="es. MK, AT"
              className={`${INPUT_BASE} tabular-nums`}
            />
          </label>
          {parsedPrefixes.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {parsedPrefixes.map((p) => (
                <span
                  key={p}
                  className="rounded-full bg-violet-soft px-2 py-0.5 tabular-nums text-[11px] font-medium text-ink"
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </section>

        <div aria-live="polite">
          <ErrorBanner message={settings.error} />
          <ErrorBanner message={error} />
          {message && !error && (
            <div className="rounded-full border border-green-soft bg-green-soft px-3 py-2 text-[12.5px] text-ink">
              {message}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-ink-2">
            File generato:{" "}
            <code className="tabular-nums text-ink-2">
              {(partitaIva.trim() || "PIVA").replace(/\D/g, "") || "PIVA"}_AAAAMMGGHHMM.csv
            </code>
          </p>
          <button
            type="submit"
            disabled={busy}
            className="rounded-full border border-accent bg-accent-2 px-3.5 py-1.5 text-[12px] font-medium text-white transition-[transform,opacity] duration-[120ms] hover:brightness-95 active:scale-[0.98] disabled:opacity-50"
          >
            {busy ? "Salvataggio…" : "Salva Cosma"}
          </button>
        </div>
      </form>
    </div>
  );
}
